import React, { useRef, useState, useMemo } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = [
  { fill: 'rgba(27, 67, 50, 0.6)', stroke: '#1B4332', label: 'dark green' },
  { fill: 'rgba(45, 106, 79, 0.6)', stroke: '#2D6A4F', label: 'medium green' },
  { fill: 'rgba(64, 145, 108, 0.6)', stroke: '#40916C', label: 'bright green' },
  { fill: 'rgba(116, 198, 157, 0.6)', stroke: '#74C69D', label: 'light green' },
  { fill: 'rgba(183, 228, 199, 0.6)', stroke: '#B7E4C7', label: 'pale green' },
];

const INVERT_KEYWORDS = ['days', 'maturity', 'flowering', 'duration', 'disease', 'pest', 'lodging', 'wilting'];
const shouldInvert = (trait) => INVERT_KEYWORDS.some(kw => trait.toLowerCase().includes(kw));

export default function MultiTraitRadar({ treatmentMeans, traits, title = '' }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(true);
  const [hidden, setHidden] = useState(new Set());
  const [tooltip, setTooltip] = useState(null);

  // Compute top 5 genotypes by first trait (usually yield)
  const { genotypes, normData, rawData, maxTraits, invertedTraits } = useMemo(() => {
    if (!treatmentMeans || !traits || traits.length < 3) return { genotypes: [], normData: {}, rawData: {}, maxTraits: [], invertedTraits: [] };

    const trtNames = Object.keys(treatmentMeans[traits[0]] || {});
    // Sort by first trait (yield) descending, take top 5
    const sorted = [...trtNames].sort((a, b) =>
      (treatmentMeans[traits[0]]?.[b] || 0) - (treatmentMeans[traits[0]]?.[a] || 0)
    );
    const top = sorted.slice(0, 5);
    const usedTraits = traits.slice(0, 8);
    const inverted = usedTraits.filter(shouldInvert);

    // Compute min/max per trait
    const mins = {}, maxs = {};
    for (const t of usedTraits) {
      const vals = trtNames.map(g => treatmentMeans[t]?.[g] || 0);
      mins[t] = Math.min(...vals);
      maxs[t] = Math.max(...vals);
    }

    // Normalize to 0-100
    const norm = {}, raw = {};
    for (const g of top) {
      norm[g] = [];
      raw[g] = [];
      for (const t of usedTraits) {
        const val = treatmentMeans[t]?.[g] || 0;
        const range = maxs[t] - mins[t] || 1;
        let n = ((val - mins[t]) / range) * 100;
        if (inverted.includes(t)) n = 100 - n;
        norm[g].push(Math.round(n * 100) / 100);
        raw[g].push(Math.round(val * 100) / 100);
      }
    }

    // Grand mean polygon
    norm['Grand Mean'] = [];
    raw['Grand Mean'] = [];
    for (let ti = 0; ti < usedTraits.length; ti++) {
      const avg = trtNames.reduce((s, g) => s + (treatmentMeans[usedTraits[ti]]?.[g] || 0), 0) / trtNames.length;
      const range = maxs[usedTraits[ti]] - mins[usedTraits[ti]] || 1;
      let n = ((avg - mins[usedTraits[ti]]) / range) * 100;
      if (inverted.includes(usedTraits[ti])) n = 100 - n;
      norm['Grand Mean'].push(Math.round(n * 100) / 100);
      raw['Grand Mean'].push(Math.round(avg * 100) / 100);
    }

    return { genotypes: top, normData: norm, rawData: raw, maxTraits: usedTraits, invertedTraits: inverted };
  }, [treatmentMeans, traits]);

  if (!genotypes.length || maxTraits.length < 3) return null;

  const n = maxTraits.length;
  const cx = 200, cy = 200, r = 150;
  const angleStep = (2 * Math.PI) / n;
  const angles = Array.from({ length: n }, (_, i) => -Math.PI / 2 + i * angleStep);

  const toXY = (angle, val) => ({
    x: cx + (val / 100) * r * Math.cos(angle),
    y: cy + (val / 100) * r * Math.sin(angle),
  });

  const polygonPath = (values) => {
    return values.map((v, i) => {
      const { x, y } = toXY(angles[i], v);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ') + 'Z';
  };

  // Grid circles
  const gridLevels = [20, 40, 60, 80, 100];

  const handleExport = async () => {
    try {
      const svgEl = canvasRef.current;
      if (!svgEl) return;
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 1200;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, 1200, 1200);
        ctx.drawImage(img, 0, 0, 1200, 1200);
        const link = document.createElement('a');
        link.download = 'radar_chart.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Radar chart downloaded!');
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch {
      toast.error('Export failed');
    }
  };

  const toggleGenotype = (g) => {
    setHidden(prev => {
      const next = new Set(prev);
      next.has(g) ? next.delete(g) : next.add(g);
      return next;
    });
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="radar-chart-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 transition-colors"
        style={{ background: '#F0F7F3', borderBottom: open ? '1px solid var(--border-color)' : 'none' }}>
        <span className="font-body text-sm font-bold" style={{ color: 'var(--green-900)' }}>
          {title || 'Trait Performance Profile — Radar Chart'}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="p-4">
          <div className="flex justify-center">
            <svg ref={canvasRef} viewBox="0 0 400 400" width="400" height="400" style={{ maxWidth: '100%' }}>
              {/* Grid */}
              {gridLevels.map(level => (
                <polygon key={level}
                  points={angles.map(a => `${cx + (level / 100) * r * Math.cos(a)},${cy + (level / 100) * r * Math.sin(a)}`).join(' ')}
                  fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
              ))}
              {/* Axes */}
              {angles.map((a, i) => (
                <g key={i}>
                  <line x1={cx} y1={cy} x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)} stroke="#E2E8F0" strokeWidth="0.5" />
                  <text x={cx + (r + 16) * Math.cos(a)} y={cy + (r + 16) * Math.sin(a)}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize="8" fontFamily="Inter, sans-serif" fill="#4A5568" fontWeight="600">
                    {maxTraits[i].length > 12 ? maxTraits[i].slice(0, 12) + '…' : maxTraits[i]}
                    {invertedTraits.includes(maxTraits[i]) ? ' ↓' : ''}
                  </text>
                </g>
              ))}
              {/* Grand Mean dashed */}
              {!hidden.has('Grand Mean') && normData['Grand Mean'] && (
                <polygon
                  points={normData['Grand Mean'].map((v, i) => { const p = toXY(angles[i], v); return `${p.x},${p.y}`; }).join(' ')}
                  fill="none" stroke="#888" strokeWidth="1" strokeDasharray="4,3" opacity="0.6" />
              )}
              {/* Genotype polygons */}
              {genotypes.map((g, gi) => {
                if (hidden.has(g)) return null;
                const color = COLORS[gi % COLORS.length];
                return (
                  <g key={g}>
                    <polygon
                      points={normData[g].map((v, i) => { const p = toXY(angles[i], v); return `${p.x},${p.y}`; }).join(' ')}
                      fill={color.fill} stroke={color.stroke} strokeWidth="1.5" />
                    {normData[g].map((v, i) => {
                      const p = toXY(angles[i], v);
                      return (
                        <circle key={i} cx={p.x} cy={p.y} r="3" fill={color.stroke} stroke="white" strokeWidth="1"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={() => setTooltip({ x: p.x, y: p.y, g, trait: maxTraits[i], norm: v, raw: rawData[g][i] })}
                          onMouseLeave={() => setTooltip(null)} />
                      );
                    })}
                  </g>
                );
              })}
              {/* Tooltip */}
              {tooltip && (
                <g>
                  <rect x={tooltip.x + 8} y={tooltip.y - 28} width="120" height="32" rx="4" fill="white" stroke="#E2E8F0" />
                  <text x={tooltip.x + 14} y={tooltip.y - 16} fontSize="7" fill="#1A1A2E" fontFamily="Inter"><tspan fontWeight="bold">{tooltip.g}</tspan> — {tooltip.trait}</text>
                  <text x={tooltip.x + 14} y={tooltip.y - 6} fontSize="7" fill="#4A5568" fontFamily="JetBrains Mono">Actual: {tooltip.raw} | Norm: {tooltip.norm}</text>
                </g>
              )}
              {/* Grid labels */}
              {gridLevels.map(level => (
                <text key={level} x={cx + 2} y={cy - (level / 100) * r + 3} fontSize="6" fill="#A0AEC0" fontFamily="Inter">{level}</text>
              ))}
            </svg>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-3 flex-wrap mt-3">
            {genotypes.map((g, gi) => {
              const color = COLORS[gi % COLORS.length];
              const isHidden = hidden.has(g);
              return (
                <button key={g} onClick={() => toggleGenotype(g)}
                  className="flex items-center gap-1.5 text-xs font-body font-semibold px-2 py-1 rounded transition-all"
                  style={{ opacity: isHidden ? 0.3 : 1, border: `1px solid ${color.stroke}20` }}
                  data-testid={`radar-legend-${g}`}>
                  <span className="w-3 h-3 rounded-sm" style={{ background: color.fill, border: `1px solid ${color.stroke}` }} />
                  {g}
                </button>
              );
            })}
            <button onClick={() => toggleGenotype('Grand Mean')}
              className="flex items-center gap-1.5 text-xs font-body font-semibold px-2 py-1 rounded transition-all"
              style={{ opacity: hidden.has('Grand Mean') ? 0.3 : 1, border: '1px solid #88888820' }}
              data-testid="radar-legend-mean">
              <span className="w-3 h-0.5 bg-gray-400" style={{ borderTop: '1px dashed #888' }} />
              Grand Mean
            </button>
          </div>

          {/* Caption + Export */}
          <div className="mt-3 text-center">
            <p className="font-body text-[10px] italic" style={{ color: 'var(--text-light)' }}>
              Values normalized to 0-100 scale for comparison. Higher values indicate better performance.
              {invertedTraits.length > 0 && ` ${invertedTraits.join(', ')} scale inverted (↓ = lower is better).`}
            </p>
            <button onClick={handleExport}
              className="flex items-center gap-1.5 mx-auto mt-2 font-body btn-press text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ border: '1px solid var(--border-color)', color: 'var(--text-medium)' }}
              data-testid="radar-export-btn">
              <Download size={11} /> Download as PNG
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
